import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useOrganization, MODULES } from '@/hooks/useOrganization';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { 
  Users, 
  UserPlus, 
  Crown, 
  Shield, 
  User,
  Trash2,
  Settings,
  ArrowLeft,
  Building2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import InviteMemberDialog from '@/components/team/InviteMemberDialog';
import MemberPermissionsDialog from '@/components/team/MemberPermissionsDialog';
import MemberCard from '@/components/team/MemberCard';

export default function TeamSettings() {
  const { user } = useAuth();
  const { 
    organization, 
    members, 
    currentMember,
    memberPermissions,
    dataAssignments,
    loading, 
    isOwner,
    canManageMembers,
    removeMember,
    updateMemberRole,
    updateMemberPermissions,
    updateDataAssignments,
    updateOrganizationName,
  } = useOrganization();

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);

  const activeMembers = members.filter(m => m.accepted_at || m.role === 'owner');
  const pendingMembers = members.filter(m => !m.accepted_at && m.role !== 'owner');
  const memberCount = activeMembers.length;
  const maxMembers = organization?.max_members || 3;

  const handleSaveOrgName = async () => {
    if (orgName.trim() && organization) {
      await updateOrganizationName(orgName.trim());
      setIsEditingName(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Gerenciar Equipe</h1>
            <p className="text-muted-foreground">
              Gerencie os usuários e permissões da sua organização
            </p>
          </div>
        </div>

        {/* Organization Info */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <CardTitle>Organização</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditingName ? (
              <div className="flex gap-2">
                <Input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Nome da organização"
                />
                <Button onClick={handleSaveOrgName}>Salvar</Button>
                <Button variant="outline" onClick={() => setIsEditingName(false)}>
                  Cancelar
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{organization?.name || 'Minha Empresa'}</p>
                  <p className="text-sm text-muted-foreground">
                    {memberCount} de {maxMembers} usuários
                  </p>
                </div>
                {isOwner && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setOrgName(organization?.name || '');
                      setIsEditingName(true);
                    }}
                  >
                    Editar nome
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Members */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <CardTitle>Membros da Equipe</CardTitle>
              </div>
              {canManageMembers && memberCount < maxMembers && (
                <Button onClick={() => setIsInviteOpen(true)} size="sm">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Convidar Usuário
                </Button>
              )}
            </div>
            <CardDescription>
              {canManageMembers 
                ? 'Gerencie os usuários que têm acesso à sua organização'
                : 'Veja os membros da sua organização'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Active Members */}
            {activeMembers.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                currentUserId={user?.id || ''}
                canManage={canManageMembers}
                permissions={memberPermissions[member.id] || []}
                currentUserRole={currentMember?.role}
                onRemove={() => removeMember(member.id)}
                onEditPermissions={() => setEditingMemberId(member.id)}
                onRoleChange={(role) => updateMemberRole(member.id, role)}
              />
            ))}

            {/* Pending Invites */}
            {pendingMembers.length > 0 && (
              <>
                <Separator className="my-4" />
                <p className="text-sm font-medium text-muted-foreground">
                  Convites Pendentes
                </p>
                {pendingMembers.map((member) => (
                  <MemberCard
                    key={member.id}
                    member={member}
                    currentUserId={user?.id || ''}
                    canManage={canManageMembers}
                    permissions={[]}
                    isPending
                    currentUserRole={currentMember?.role}
                    onRemove={() => removeMember(member.id)}
                  />
                ))}
              </>
            )}

            {members.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                Nenhum membro encontrado
              </p>
            )}
          </CardContent>
        </Card>

        {/* Permissions Legend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Legenda de Funções</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="flex items-start gap-2">
                <Crown className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Proprietário</p>
                  <p className="text-sm text-muted-foreground">
                    Acesso total. Gerencia membros e configurações.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Shield className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Administrador</p>
                  <p className="text-sm text-muted-foreground">
                    Acesso total aos dados, mas não gerencia membros.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="h-5 w-5 text-gray-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Membro</p>
                  <p className="text-sm text-muted-foreground">
                    Acesso limitado conforme permissões definidas.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <InviteMemberDialog
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        maxMembers={maxMembers}
        currentCount={memberCount}
      />

      {editingMemberId && (
        <MemberPermissionsDialog
          open={!!editingMemberId}
          onOpenChange={(open) => !open && setEditingMemberId(null)}
          memberId={editingMemberId}
          member={members.find(m => m.id === editingMemberId)}
          permissions={memberPermissions[editingMemberId] || []}
          dataAssignments={dataAssignments[editingMemberId] || []}
          onSavePermissions={(perms) => updateMemberPermissions(editingMemberId, perms)}
          onSaveAssignments={(table, ids) => updateDataAssignments(editingMemberId, table, ids)}
        />
      )}
    </div>
  );
}
